import { useRef, useState, useEffect } from "react";
import IconArrow from "../../../components/icons/icon-arrow";
import { ButtonCustom } from "../../../components/custom/button-custom";

interface VerifyOtpComponentProps {
  setValueOtp: (value: string) => void;
  isLoading?: boolean;
  backWardForm: () => void;
}

export const VerifyOtpComponent = ({ isLoading, setValueOtp, backWardForm }: VerifyOtpComponentProps) => {
  const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(""));
  const [isShowError, setIsShowError] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Add useEffect to ensure the first input is focused on mount
  useEffect(() => {
    // Small timeout to ensure the DOM is fully rendered
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSendOtp = () => {
    if (otpCode.includes("")) {
      setIsShowError(true);
      return;
    }
    setIsShowError(false);
    setValueOtp(otpCode.join(""));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      handleSendOtp();
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      if (index > 0 && !otpCode[index]) {
        // Move focus to the previous input
        inputRefs.current[index - 1]?.focus();
      }

      setOtpCode((prevOtpCode) => {
        const newOtpCode = [...prevOtpCode];
        newOtpCode[index] = ""; // Clear the current input value
        return newOtpCode;
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;

    if (/^\d*$/.test(value)) {
      setOtpCode((prevOtpCode) => {
        const newOtpCode = [...prevOtpCode];
        newOtpCode[index] = value;
        return newOtpCode;
      });

      // Move focus to the next input
      if (value !== "" && index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, currentIndex: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    // Extract only digits
    const digits = pastedData.replace(/[^\d]/g, "");
    // Calculate how many empty slots are left from current position
    const remainingSlots = 6 - currentIndex;
    const digitsToFill = digits.slice(0, remainingSlots);
    if (digitsToFill.length > 0) {
      const newOtpCode = [...otpCode];
      digitsToFill.split("").forEach((digit, index) => {
        const targetIndex = currentIndex + index;
        if (targetIndex < 6) {
          newOtpCode[targetIndex] = digit;
        }
      });
      setOtpCode(newOtpCode);
      // Focus on next empty input or last filled input
      const nextIndex = Math.min(currentIndex + digitsToFill.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <>
      <header className="mb-8 animate-fade-in-up">
        <ButtonCustom className="text-black" onClick={() => backWardForm()} component={<IconArrow className="w-6 h-6 fill-gray-600" />} />
        <h1 className="mb-1 text-2xl font-bold text-center text-blue-600">Xác minh mã bảo mật lớp thứ 2</h1>
        <p className="text-base text-center text-slate-500">Nhập mã bảo mật lớp thứ 2 gồm 6 chữ số trong ứng dụng điện thoại của bạn.</p>
      </header>
      <form className="space-y-5 animate-fade-in-up" data-testid="verify-otp-form">
        <div className="flex items-center justify-center gap-3 xl:flex-nowrap md:flex-nowrap sm:flex-wrap max-[500px]:flex-wrap">
          {Array.from({ length: 6 }, (_, index) => (
            <input
              key={index}
              type="text"
              ref={(el) => (inputRefs.current[index] = el)}
              className="p-4 w-14 h-14 text-2xl text-center bg-gray-200 rounded border border-transparent appearance-none outline-none text-slate-900 hover:border-slate-200 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              maxLength={1}
              value={otpCode[index]}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onChange={(e) => handleChange(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              data-testid={`input-otp-${index}`}
              style={{ fontWeight: "800" }}
              // Adding tabIndex to ensure proper tab order
              tabIndex={index + 1}
            />
          ))}
        </div>
        {isShowError && <p className="text-center text-red-500">Vui lòng nhập đầy đủ mã xác minh</p>}
        <ButtonCustom
          text="Xác minh tài khoản"
          onClick={handleSendOtp}
          isLoading={isLoading}
          className="btn btn-gradient w-full border-0 shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
        />
      </form>
    </>
  );
};
